"use client";

import { useState } from 'react';
import { Button } from "@/components/ui/button";
import { RotateCcw, Download, CheckCircle, XCircle } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { ExamResult, QuestionSummaryResult } from "@/lib/types";
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog";
import renderFormattedText from "@/components/shared/renderFormattedText";

interface ExamResultSummaryProps {
    finalExamScore: number;
    finalExamMaxPossibleScore: number;
    totalQuestions: number;
    timeTaken: number; // seconds
    examDuration?: number; // seconds, relevant for Exam mode
    mode: 'exam' | 'debug';
    questionSummaryForDisplay: QuestionSummaryResult[];
    onClose?: () => void;
    onDownload?: () => void; // Optional if parent handles it, but default logic provided here
    attemptedCount: number;
}

const ExamResultSummary = ({
    finalExamScore,
    finalExamMaxPossibleScore,
    totalQuestions,
    timeTaken,
    examDuration,
    mode,
    questionSummaryForDisplay,
    onClose,
    attemptedCount
}: ExamResultSummaryProps) => {

    const [selectedQuestionSummary, setSelectedQuestionSummary] = useState<QuestionSummaryResult | null>(null);

    const PASS_THRESHOLD = 0.55;
    const finalPercentage = finalExamMaxPossibleScore > 0 ? (finalExamScore / finalExamMaxPossibleScore) * 100 : 0;
    const finalPassed = finalExamMaxPossibleScore > 0 ? (finalExamScore / finalExamMaxPossibleScore) >= PASS_THRESHOLD : false;
    // const progressPercentage = totalQuestions > 0 ? (attemptedCount / totalQuestions) * 100 : 0; // Not used in this visual, logic in parent usually

    const handleDownloadResults = () => {
        const doc = new jsPDF();
        const titleMode = mode === 'exam' ? 'Exam Mode' : 'Full Mode';
        const timestamp = new Date();
        const dateStr = timestamp.toLocaleDateString().replace(/\//g, '-');
        const timeStr = timestamp.toLocaleTimeString().replace(/:/g, '-');
        const filename = `Comenius Exam Results - ${titleMode} - ${totalQuestions} - ${dateStr} ${timeStr}.pdf`;

        doc.setFontSize(18);
        doc.setTextColor(0, 51, 102);
        doc.text("Comenius Exam Results", 14, 22);

        doc.setFontSize(12);
        doc.setTextColor(0, 0, 0);
        doc.text(`Mode: ${titleMode}`, 14, 32);
        doc.text(`Date: ${timestamp.toLocaleString()}`, 14, 38);
        doc.text(`Score: ${finalExamScore.toFixed(0)} / ${finalExamMaxPossibleScore.toFixed(0)} (${finalPercentage.toFixed(1)}%)`, 14, 44);

        if (mode === 'exam' || timeTaken > 0) {
            const m = Math.floor(timeTaken / 60);
            const s = Math.abs(timeTaken % 60);
            doc.text(`Time Taken: ${m}m ${s}s ${(examDuration && timeTaken > examDuration) ? '(Overtime)' : ''}`, 14, 50);
        }

        doc.text(`Result: ${finalPassed ? 'APPROVED' : 'NOT APPROVED'}`, 14, (mode === 'exam' || timeTaken > 0) ? 56 : 50);

        const tableRows = questionSummaryForDisplay.map(q => [
            q.displayNumber,
            q.questionSubject,
            q.isOverallCorrect ? 'Correct' : 'Incorrect',
            q.isOverallCorrect ? 'All Correct' : `${q.statementsFeedback.filter((s) => !s.wasCorrect).length} errors`
        ]);

        autoTable(doc, {
            startY: (mode === 'exam' || timeTaken > 0) ? 65 : 60,
            head: [['#', 'Subject', 'Status', 'Details']],
            body: tableRows,
            theme: 'striped',
            headStyles: { fillColor: [0, 51, 102] },
        });

        doc.save(filename);
    };

    return (
        <div className="text-center space-y-5 p-5 bg-card rounded-lg shadow-xl max-w-xl mx-auto w-full">
            <h2 className="text-3xl font-bold text-primary">Exam Result</h2>

            <div className="mt-3 p-4 border rounded-lg bg-background">
                <p className="text-lg font-semibold">Final Score:</p>
                <p className="text-4xl font-bold text-primary">{finalExamScore.toFixed(0)} / {finalExamMaxPossibleScore.toFixed(0)}</p>
                <p className="text-md mt-1">General Percentage: {finalPercentage.toFixed(1)}%</p>
                <p className={`text-xl font-semibold mt-1 ${finalPassed ? 'text-green-600' : 'text-red-600'}`}>
                    General Result: {finalPassed ? 'Approved' : 'Not Approved'}
                </p>

                {(mode === 'exam' || timeTaken > 0) && (
                    <div className="mt-2 pt-2 border-t">
                        <p className="text-sm text-muted-foreground">Time Taken:</p>
                        <p className="text-lg font-mono">
                            {Math.floor(timeTaken / 60)}m {Math.abs(timeTaken % 60)}s
                            {examDuration && timeTaken > examDuration && <span className="text-red-500 ml-2">(Overtime)</span>}
                        </p>
                    </div>
                )}
            </div>

            <div className="flex justify-center mt-2">
                <Button onClick={handleDownloadResults} variant="outline" className="flex items-center gap-2">
                    <Download className="w-4 h-4" /> Download Results
                </Button>
            </div>

            {questionSummaryForDisplay.length > 0 && (
                <div className="mt-4">
                    <h3 className="text-xl font-semibold text-primary mb-3">Detailed Summary</h3>
                    <div className="grid grid-cols-5 sm:grid-cols-8 md:grid-cols-10 gap-1.5 max-w-md mx-auto p-3 border rounded-md bg-muted/20 max-h-60 overflow-y-auto">
                        {questionSummaryForDisplay.map((summary) => (
                            <button
                                key={summary.questionId}
                                title={`Question ${summary.displayNumber}`}
                                className={`p-1.5 aspect-square flex items-center justify-center border rounded text-xs font-medium shadow-sm
                  ${summary.isOverallCorrect ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-red-600 text-white hover:bg-red-700'}
                  transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1`}
                                onClick={() => setSelectedQuestionSummary(summary)}
                            >
                                {summary.displayNumber}
                            </button>
                        ))}
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">Click on a number to see details.</p>
                </div>
            )}

            {onClose && (
                <Button onClick={onClose} className="mt-5 bg-accent hover:bg-accent/90 text-accent-foreground text-lg py-3 px-6 w-full sm:w-auto">
                    Close
                </Button>
            )}

            {selectedQuestionSummary && (
                <Dialog open={!!selectedQuestionSummary} onOpenChange={() => setSelectedQuestionSummary(null)}>
                    <DialogContent className="sm:max-w-lg md:max-w-xl max-h-[85svh] flex flex-col">
                        <div className="flex-grow min-h-0 overflow-y-auto pr-3 py-2 scroll-smooth">
                            <DialogHeader>
                                <DialogTitle className="text-primary">
                                    {selectedQuestionSummary.questionSubject} Question {selectedQuestionSummary.displayNumber} detail:
                                </DialogTitle>
                                <DialogDescription className="text-sm text-foreground text-left pt-1">
                                    {renderFormattedText({ text: selectedQuestionSummary.questionText })}
                                </DialogDescription>
                            </DialogHeader>

                            <div className="space-y-3 mt-4">
                                {selectedQuestionSummary.statementsFeedback.map((feedback, index) => (
                                    <div key={feedback.statementId} className={`p-3 rounded-md border ${feedback.wasCorrect ? 'border-green-500 bg-green-500/10' : 'border-red-500 bg-red-500/10'}`}>
                                        <div className="text-sm font-medium text-foreground flex items-start">
                                            <span className="mr-1.5">{`${String.fromCharCode(97 + index)}. `}</span>
                                            <div className="flex-1">{renderFormattedText({ text: feedback.statementText })}</div>
                                        </div>
                                        <div className="mt-2 text-xs space-y-1 pl-4 border-l-2 ml-1.5 border-gray-300 dark:border-gray-600">
                                            <p>Your answer: <span className="font-semibold">{feedback.userAnswerSelected ? 'Checked (True)' : 'Unmarked (False)'}</span></p>
                                            <p>Correct answer: <span className="font-semibold">{feedback.statementActualTruth ? 'True' : 'False'}</span></p>
                                        </div>
                                        <div className="mt-2 flex items-center pl-4">
                                            {feedback.wasCorrect ? (
                                                <CheckCircle className="w-4 h-4 text-green-600 mr-1.5" />
                                            ) : (
                                                <XCircle className="w-4 h-4 text-red-600 mr-1.5" />
                                            )}
                                            <span className={`text-xs font-medium ${feedback.wasCorrect ? 'text-green-700' : 'text-red-700'}`}>
                                                {feedback.wasCorrect ? 'Correct' : 'Incorrect'} selection
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <DialogFooter className="mt-auto pt-3 border-t">
                            <Button onClick={() => setSelectedQuestionSummary(null)} variant="outline">Close Details</Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            )}
        </div>
    );
};

export default ExamResultSummary;
